/* eslint-disable react/prop-types */
import { Currency, Price, Token } from '@kodiak-finance/sdk-core'
import { FeeAmount } from '@kodiak-finance/v3-sdk'
import CurrencyLogo from 'components/CurrencyLogo'
import Loader from 'components/Loader'
import { DynamicSizedLoadingRow } from 'components/Loader/styled'
import React, { useMemo } from 'react'
import { Bound } from 'state/islands/mint/actions'
import styled from 'styled-components'
import { KodiakFarm, StakingTokenType } from 'types/farms'
import { formatPrice } from 'utils/formatCurrencyAmount'
import { formatTickPrice, formatTickPriceForRangeDisplay, getOptimalSigFigs } from 'utils/formatTickPrice'

const PriceInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  // margin-left: auto;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    width: 100%;
    justify-content: space-between;
    margin-top: 12px;
    margin-left: 0;
  `};

  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-direction: column;
    width: 100%;
    align-items: stretch;
    gap: 8px;
  `};
`

const PriceInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  background: rgba(21, 26, 47, 0.5);
  backdrop-filter: blur(5px);
  border-radius: 8px;
  padding: 8px 12px;
  border: 1px dotted ${({ theme }) => theme.backgroundInteractive};
  min-width: 180px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    flex: 1;
    min-width: 0;
  `};

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
    padding: 10px;
  `};
`

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    text-align: left;
    font-size: 10px;
    margin-bottom: 6px;
  `};
`

const PriceItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    justify-content: space-between;
  `};
`

const PriceLabel = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  min-width: 32px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 11px;
  `};
`

const PriceValue = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 13px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 12px;
  `};

  & > * {
    margin-right: 1px;
  }
`

const InfinitySymbol = styled.span`
  font-size: 16px;
  line-height: 1;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 12px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    flex-direction: column;
    align-items: flex-start;
  `};
`

const HeaderContent = styled.div`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
  `};
`

interface ProcessedData {
  currencyQuote: Currency | null | undefined
  currencyBase: Currency | null | undefined
  priceLower: Price<Token, Token> | null | undefined
  priceUpper: Price<Token, Token> | null | undefined
  quotePrice: Price<Token, Token> | null | undefined
  positionSummaryLink: string
  currencyA?: Currency | null | undefined
  currencyB?: Currency | null | undefined
  farmAddress?: string
  isLowerPriceAtLimit: boolean | undefined
  isUpperPriceAtLimit: boolean | undefined
  feeTier?: number
}

interface FormattedPrices {
  lower: string | null
  upper: string | null
  quote: string | null
}

export const InlinePriceInfo: React.FC<{
  farm?: KodiakFarm | null
  processedData: ProcessedData | null
  tickAtLimit: { [bound in Bound]?: boolean | undefined }
  poolType: 'Generic' | 'V2' | 'Island' | 'Charm' | null
}> = ({ processedData, tickAtLimit, poolType }) => {
  const formattedPrices = useMemo<FormattedPrices>(() => {
    if (!processedData) {
      return { lower: null, upper: null, quote: null }
    }
    const lowerDisplay = formatTickPriceForRangeDisplay(
      processedData.priceLower,
      processedData.isLowerPriceAtLimit,
      Bound.LOWER,
      processedData.feeTier
    )

    const upperDisplay = formatTickPriceForRangeDisplay(
      processedData.priceUpper,
      processedData.isUpperPriceAtLimit,
      Bound.UPPER,
      processedData.feeTier
    )

    return {
      lower: lowerDisplay,
      upper: upperDisplay,
      quote: processedData.quotePrice
        ? processedData.feeTier === FeeAmount.LOWEST
          ? processedData.quotePrice.toSignificant(5)
          : formatPrice(processedData.quotePrice, getOptimalSigFigs(processedData.quotePrice))
        : null,
    }
  }, [processedData])

  if (processedData === null || poolType === 'Generic') {
    return null
  }

  const { quotePrice, currencyQuote, currencyBase, priceLower, priceUpper } = processedData
  const isV2Pool = poolType === 'V2'

  return (
    <PriceInfoRow>
      {isV2Pool ? (
        <PriceInfoSection>
          <SectionTitle>Price Range</SectionTitle>
          <PriceItemRow>
            <PriceLabel>Min:</PriceLabel>
            <PriceValue>
              <CurrencyLogo size="16px" currency={currencyBase} />
              <span>1 =</span>
              <CurrencyLogo size="16px" currency={currencyQuote} />
              <span>0</span>
            </PriceValue>
          </PriceItemRow>
          <PriceItemRow>
            <PriceLabel>Max:</PriceLabel>
            <PriceValue>
              <CurrencyLogo size="16px" currency={currencyBase} />
              <span>1 =</span>
              <CurrencyLogo size="16px" currency={currencyQuote} />
              <InfinitySymbol>∞</InfinitySymbol>
            </PriceValue>
          </PriceItemRow>
        </PriceInfoSection>
      ) : (
        priceLower &&
        priceUpper && (
          <PriceInfoSection>
            <SectionTitle>Price Range</SectionTitle>
            <PriceItemRow>
              <PriceLabel>Min:</PriceLabel>
              <PriceValue>
                <CurrencyLogo size="16px" currency={currencyBase} />
                <span>1 =</span>
                <CurrencyLogo size="16px" currency={currencyQuote} />
                <span>{formattedPrices.lower}</span>
              </PriceValue>
            </PriceItemRow>
            <PriceItemRow>
              <PriceLabel>Max:</PriceLabel>
              <PriceValue>
                <CurrencyLogo size="16px" currency={currencyBase} />
                <span>1 =</span>
                <CurrencyLogo size="16px" currency={currencyQuote} />
                <span>{formattedPrices.upper}</span>
              </PriceValue>
            </PriceItemRow>
          </PriceInfoSection>
        )
      )}

      {quotePrice && (
        <PriceInfoSection>
          <SectionTitle>Current Price</SectionTitle>
          <PriceItemRow>
            <PriceValue>
              <CurrencyLogo size="16px" currency={currencyBase} />
              <span>1 =</span>
              <CurrencyLogo size="16px" currency={currencyQuote} />
              <span>
                {formattedPrices.quote} {currencyQuote?.symbol}
              </span>
            </PriceValue>
          </PriceItemRow>
        </PriceInfoSection>
      )}
    </PriceInfoRow>
  )
}

export const RangeDisplay: React.FC<{
  farm: KodiakFarm | null | undefined
  processedData: ProcessedData | null
  tickAtLimit: { [bound in Bound]?: boolean | undefined }
  token0: Token | null | undefined
  token1: Token | null | undefined
  poolType: 'Generic' | 'V2' | 'Island' | null
  feeTier?: number
}> = ({ farm, processedData, tickAtLimit, poolType, feeTier }) => {
  if (processedData === null) {
    return <Loader />
  }

  if (farm?.stakingTokenType === StakingTokenType.GENERIC) {
    return <DynamicSizedLoadingRow />
  }

  const isV2Pool = poolType === 'V2'
  const { currencyQuote, currencyBase } = processedData

  if (isV2Pool) {
    return (
      <PriceInfoSection>
        <SectionTitle>Price Range</SectionTitle>
        <PriceItemRow>
          <PriceLabel>Min:</PriceLabel>
          <PriceValue>
            <CurrencyLogo size="16px" currency={currencyBase} />
            <span>1 =</span>
            <CurrencyLogo size="16px" currency={currencyQuote} />
            <span>0</span>
          </PriceValue>
        </PriceItemRow>
        <PriceItemRow>
          <PriceLabel>Max:</PriceLabel>
          <PriceValue>
            <CurrencyLogo size="16px" currency={currencyBase} />
            <span>1 =</span>
            <CurrencyLogo size="16px" currency={currencyQuote} />
            <InfinitySymbol>∞</InfinitySymbol>
          </PriceValue>
        </PriceItemRow>
      </PriceInfoSection>
    )
  }

  const { priceLower, priceUpper } = processedData

  if (!priceLower || !priceUpper) {
    return <DynamicSizedLoadingRow />
  }

  return (
    <PriceInfoSection>
      <SectionTitle>Price Range</SectionTitle>
      <PriceItemRow>
        <PriceLabel>Min:</PriceLabel>
        <PriceValue>
          <CurrencyLogo size="16px" currency={currencyBase} />
          <span>1 =</span>
          <CurrencyLogo size="16px" currency={currencyQuote} />
          <span>{formatTickPrice(priceLower, tickAtLimit, Bound.LOWER, feeTier)}</span>
        </PriceValue>
      </PriceItemRow>
      <PriceItemRow>
        <PriceLabel>Max:</PriceLabel>
        <PriceValue>
          <CurrencyLogo size="16px" currency={currencyBase} />
          <span>1 =</span>
          <CurrencyLogo size="16px" currency={currencyQuote} />
          <span>{formatTickPrice(priceUpper, tickAtLimit, Bound.UPPER, feeTier)}</span>
        </PriceValue>
      </PriceItemRow>
    </PriceInfoSection>
  )
}

export const PriceDisplay: React.FC<{
  processedData: ProcessedData | null
  title?: string
  poolType: 'Generic' | 'V2' | 'Island' | null
}> = ({ processedData, poolType }) => {
  if (!(poolType + 'Pool' === StakingTokenType.ISLAND)) return ''

  if (processedData === null) {
    return <Loader />
  }

  const { quotePrice, currencyBase, currencyQuote } = processedData

  if (!quotePrice) {
    return <DynamicSizedLoadingRow />
  }

  return (
    <PriceInfoSection>
      <SectionTitle>Current Price</SectionTitle>
      <PriceItemRow>
        <PriceValue>
          <CurrencyLogo size="16px" currency={currencyBase} />
          <span>1 =</span>
          <CurrencyLogo size="16px" currency={currencyQuote} />
          <span>
            {formatPrice(quotePrice, getOptimalSigFigs(quotePrice))} {currencyQuote?.symbol}
          </span>
        </PriceValue>
      </PriceItemRow>
    </PriceInfoSection>
  )
}

export const HeaderWithPriceInfo: React.FC<{
  farm?: KodiakFarm | null
  processedData: ProcessedData | null
  tickAtLimit: { [bound in Bound]?: boolean | undefined }
  poolType: 'Generic' | 'V2' | 'Island' | 'Charm' | null
  children: React.ReactNode
}> = ({ processedData, tickAtLimit, poolType, children }) => {
  if (poolType === 'V2') return <HeaderRow>{children}</HeaderRow>
  return (
    <HeaderRow>
      <HeaderContent>{children}</HeaderContent>
      <InlinePriceInfo processedData={processedData} tickAtLimit={tickAtLimit} poolType={poolType} />
    </HeaderRow>
  )
}

export default {
  HeaderWithPriceInfo,
  InlinePriceInfo,
  RangeDisplay,
  PriceDisplay,
}
